import { AoInstance } from "../../ao/ao"
import { PoolLiquidity } from "../../ao/monitor-api"
import { PoolOverview } from "../../ao/router"
import { TokenDetails } from "../../ao/tokendrop"
import ENV from "../../env"
import { DataSource, PoolApiRow, PoolByTokensResult } from "../../types/data-source"
import { createLogger } from "../../utils/logger"
import { supabase } from "../../utils/supabase"

const logger = createLogger("SUPABASE-DATA-SOURCE")

interface SupabaseConfig {
  /** e.g. https://project-ref.supabase.co (NO trailing slash) */
  url: string
  /** anon or service_role key so Edge Function will run */
  apiKey: string
}

/**
 * Lightweight data-source that calls Supabase Edge Functions
 * through plain `fetch` instead of the Supabase JS client.
 */
export class SupabaseDataSource {
  private readonly cfg: SupabaseConfig

  constructor(_ao: AoInstance, cfg: Partial<SupabaseConfig> = {}) {
    this.cfg = {
      url: (cfg.url ?? ENV.VITE_SUPABASE_URL ?? "").replace(/\/$/, ""),
      apiKey: cfg.apiKey ?? ENV.VITE_SUPABASE_ANON_KEY ?? "",
    }

    if (!this.cfg.url || !this.cfg.apiKey) {
      throw new Error("SupabaseDataSource needs SUPABASE_URL and SUPABASE_ANON_KEY")
    }
  }

  /* ---------- DataSource methods ---------- */

  async getPoolsOverview(): Promise<PoolOverview[]> {
    logger.log("Fetching pools overview via fetch /overview")

    const response = await fetch(`${this.cfg.url}/functions/v1/overview`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.cfg.apiKey}`,
      },
      body: JSON.stringify({}),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()

    if (!data) {
      throw new Error("No response from Get-Overview (pools)")
    }

    if (!Array.isArray(data)) return []

    return data.map((pool) => ({
      quote_token_process: pool.token0_process,
      quote_token_ticker: pool.token0_ticker,
      quote_token_name: pool.token0_name,
      base_token_process: pool.token1_process,
      base_token_ticker: pool.token1_ticker,
      base_token_name: pool.token1_name,
      tvl_rank: pool.tvl_rank ?? 0,
      tvl_in_usd: pool.liquidity_usd,
      tvl_in_quote: pool.tvl_in_quote ?? 0,
      tx_count: pool.transactions,
      apr_30d: pool.apr_30d ?? 0,
      amm_process: pool.amm_process,
      pool_fee_bps: pool.pool_fee_bps,
      liquidity_usd: pool.liquidity_usd,
    }))
  }

  /*   async getUsdPrices(tokenProcesses: string[]): Promise<Record<string, PriceRow>> {
    // Nothing to do
    if (!tokenProcesses.length) return {} as Record<string, PriceRow>

    // Run all calls in parallel but keep a lid on concurrency (p-limit is tiny)
    const limit = (fn: () => Promise<any>) => fn() // swap for p-limit(4) if you like

    const entries = await Promise.all(
      tokenProcesses.map((tp) =>
        limit(async () => {
          const { data, error } = await supabase.functions.invoke<PriceRow>("usd-price", {
            headers: {
              Authorization: `Bearer ${this.cfg.apiKey}`,
            },
            body: { processId: tp },
          })

          if (error) {
            console.warn(`usd_price failed for ${tp}:`, error.message)
            return [tp, { usd_price: null, denominator: null }] as const
          }

          return [tp, data ?? { usd_price: null, denominator: null }] as const
        }),
      ),
    )

    return Object.fromEntries(entries) as Record<string, PriceRow>
  }
 */
  async getPoolsForToken(tokenProcess: string): Promise<PoolApiRow[]> {
    const response = await fetch(`${this.cfg.url}/functions/v1/pools`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.cfg.apiKey}`,
      },
      body: JSON.stringify({}),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const poolsRaw = (await response.json()) as PoolApiRow[]
    if (!poolsRaw?.length) return []

    const pools = poolsRaw.filter(
      (p) => p.token0_process === tokenProcess || p.token1_process === tokenProcess,
    )

    if (!pools.length) {
      logger.warn(`No pools found for token ${tokenProcess}`)
      return []
    }

    return pools
  }
}
